import React from "react";
import { usePublicBootstrap } from "./usePublicBootstrap";
import BookingFlow from "./pages/booking/BookingFlow";
import RemindersOptoutView from "./pages/RemindersOptoutView";
import VisitActionView from "./pages/VisitActionView";
import SalonUnavailableView from "./pages/SalonUnavailableView";
import { BookingDraftProvider } from "./pages/booking/BookingDraftContext";
import { WidgetSessionExpiredOverlay } from "./components/WidgetSessionExpiredOverlay";
import { useTranslation } from "react-i18next";
import { usePortalContainer } from "./portal/PortalContainer";
import { applyWidgetLook, getWidgetLook } from "./look/widgetLook";

/**
 * Read the `?staff=<id>` query parameter injected by embed.js
 * from the trigger element's `data-boona-staff` attribute.
 */
function readStaffParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("staff") || null;
}

/**
 * Read the `?service=<id>` query parameter injected by embed.js
 * from the trigger element's `data-boona-service` attribute.
 */
function readServiceParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("service") || null;
}

/**
 * Read the `?category=<id>` query parameter injected by embed.js
 * from the trigger element's `data-boona-category` attribute.
 */
function readCategoryParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("category") || null;
}

/**
 * Extract the widget_host slug from the current hostname.
 * e.g. "w1033.boona.app" → "w1033"
 */
function extractWidgetHost(): string | null {
  const hostname = window.location.hostname;
  const match = hostname.match(/^(w\d+)\./);
  return match ? match[1] : null;
}

/**
 * Widget bootstrap flow:
 * 1. Extracts widgetHost from the subdomain (e.g. "w1033" from "w1033.boona.app")
 * 2. POSTs { widget_host } to /public/sessions — backend resolves to company_id
 * 3. Stores auth token, fetches company settings
 * 4. Passes resolved companyId to BookingDraftProvider for all downstream API calls
 */

export function Widget({
  apiBase = import.meta.env.VITE_API_URL ?? "https://boona.app",
  widgetHost,
}: {
  apiBase?: string;
  widgetHost?: string;
}) {
  const { t } = useTranslation();
  const resolvedHost = widgetHost ?? extractWidgetHost();

  if (!resolvedHost) {
    return (
      <pre style={{ whiteSpace: "pre-wrap" }}>
        {t(
          "widget.bootstrap.missing_host",
          'Bootstrap error: Could not determine widget host from hostname "{{hostname}}"',
          { hostname: window.location.hostname },
        )}
      </pre>
    );
  }

  const {
    ready, error, licenseExpired, unavailable, companyId,
    locations, needsLocationChoice, chooseLocation,
    waitlist, claimToken, remindersOptoutToken, visitToken,
  } = usePublicBootstrap({ apiBase, widgetHost: resolvedHost });

  React.useEffect(() => {
    import("./waitlistConfigStore").then(({ setWaitlistConfig }) =>
      setWaitlistConfig(waitlist ?? null));
  }, [waitlist]);

  // The salon's button colour (docs/codebase/widget-appearance in the backend repo):
  // two CSS variables on the shadow host, once the settings are in
  const portal = usePortalContainer();
  React.useEffect(() => {
    if (!ready) return;
    applyWidgetLook((portal as unknown as ShadowRoot | null)?.host as HTMLElement | undefined, getWidgetLook());
  }, [ready, companyId, portal]);

  // Multi-salon brand: pick a location before the booking flow
  // (docs/codebase/multi-salon/ §6.5 in the backend repo)
  if (needsLocationChoice && locations.length > 0) {
    return (
      <div className="font-sans" style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        minHeight: "50vh", padding: "2rem 1rem", color: "#374151",
      }}>
        <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: ".25rem" }}>
          {t("widget.location.title", "Choose a location")}
        </h2>
        <p style={{ marginBottom: "1.25rem", color: "#6b7280", fontSize: ".9rem" }}>
          {t("widget.location.subtitle", "This business has several locations — where would you like to book?")}
        </p>
        <div style={{ width: "100%", maxWidth: 440, display: "flex", flexDirection: "column", gap: ".6rem" }}>
          {locations.map((location) => (
            <button
              key={location.company_id}
              type="button"
              onClick={() => chooseLocation(location.company_id)}
              style={{
                textAlign: "left", background: "#ffffff", cursor: "pointer",
                border: "1px solid #e5e7eb", borderRadius: 12, padding: "0.9rem 1.1rem",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: "1rem" }}>{location.title}</div>
              {location.address ? (
                <div style={{ color: "#6b7280", fontSize: ".85rem", marginTop: 2 }}>
                  {location.address}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (unavailable) {
    return <SalonUnavailableView />;
  }

  if (licenseExpired) {
    return (
      <div className="font-sans" style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "50vh", padding: "2rem",
        textAlign: "center", color: "#374151",
      }}>
        <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>⏸️</div>
        <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: ".5rem" }}>
          {t("widget.license.expired_title", "Online booking is unavailable")}
        </h2>
        <p style={{ maxWidth: 420, color: "#6b7280" }}>
          {t("widget.license.expired_body",
            "This business's booking license has expired. Please contact the salon directly to make an appointment.")}
        </p>
      </div>
    );
  }

  return (
    <div
      className="font-sans"
    >
      {error ? (
        <pre style={{ whiteSpace: "pre-wrap" }}>
          {t("widget.bootstrap.error_prefix", "Bootstrap error")}: {String(error)}
        </pre>
      ) : ready && companyId ? (
        remindersOptoutToken ? (
          // opt-out action link renders INSTEAD of the booking flow
          // (return-reminders README §4.5 in the backend repo)
          <RemindersOptoutView token={remindersOptoutToken} />
        ) : visitToken ? (
          // the SMS confirm / cancel link (inbox plan INB5 in the backend repo)
          <VisitActionView token={visitToken} />
        ) : (
          <BookingDraftProvider companyId={companyId}>
            <BookingFlow preselectedStaffId={readStaffParam()} preselectedServiceId={readServiceParam()} preselectedCategoryId={readCategoryParam()} waitlistClaimToken={claimToken} />
          </BookingDraftProvider>
        )
      ) : (
        <div>{t("widget.bootstrap.authorizing", "Authorizing...")}</div>
      )}
      <WidgetSessionExpiredOverlay />
    </div>
  );
}
